import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
  type ComponentProps,
} from "react";

/**
 * A hung image request must not strand the session behind the loading
 * screen; the browser's own timeout is far longer than a user will wait.
 */
const MAX_HOLD_MS = 10_000;

const noop = () => undefined;

/**
 * Holds a session behind its loading screen until the first-rendered
 * transcript has fully landed: lazily loaded chunks resolved and every image
 * in it loaded (or failed). Armed once the transcript commits, so holds
 * taken during that commit are counted; settles permanently the first time
 * nothing holds it, after which later images (streamed turns) are ignored.
 */
export class TranscriptLoadGate {
  private holds = 0;
  private armed = false;
  private settled = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly listeners = new Set<() => void>();

  hold(): () => void {
    if (this.settled) return noop;
    this.holds += 1;
    this.emit();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.holds -= 1;
      this.emit();
    };
  }

  holdImage(img: HTMLImageElement): () => void {
    if (this.settled || img.complete) return noop;
    const release = this.hold();
    const done = () => {
      img.removeEventListener("load", done);
      img.removeEventListener("error", done);
      release();
    };
    img.addEventListener("load", done);
    img.addEventListener("error", done);
    return done;
  }

  arm(): void {
    if (this.armed) return;
    this.armed = true;
    this.timer = setTimeout(() => this.settle(), MAX_HOLD_MS);
    this.emit();
  }

  get isSettled(): boolean {
    return this.settled;
  }

  get isReady(): boolean {
    return this.settled || (this.armed && this.holds === 0);
  }

  settle(): void {
    if (this.settled) return;
    this.settled = true;
    this.clearTimer();
    this.emit();
  }

  dispose(): void {
    this.clearTimer();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private clearTimer(): void {
    if (this.timer === null) return;
    clearTimeout(this.timer);
    this.timer = null;
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

export const TranscriptLoadGateContext =
  createContext<TranscriptLoadGate | null>(null);

/**
 * One gate per thread. `active` is whether the transcript is rendered
 * (rather than the pane's own loading shell); the gate arms in a layout
 * effect, which runs after the transcript's refs and layout effects have
 * registered their holds. `skip` settles it outright for a transcript with
 * nothing to fetch (an optimistic first send, whose attachments are inline
 * data and whose handoff animation must not be covered).
 */
export function useTranscriptLoadGate(
  threadKey: string | null,
  { active, skip = false }: { active: boolean; skip?: boolean },
): { gate: TranscriptLoadGate; ready: boolean } {
  const [current, setCurrent] = useState(() => ({
    key: threadKey,
    gate: new TranscriptLoadGate(),
  }));
  let gate = current.gate;
  if (current.key !== threadKey) {
    gate = new TranscriptLoadGate();
    setCurrent({ key: threadKey, gate });
  }
  useEffect(() => () => gate.dispose(), [gate]);

  const ready = useSyncExternalStore(
    gate.subscribe,
    () => gate.isReady,
    () => true,
  );
  useLayoutEffect(() => {
    if (skip) gate.settle();
    else if (active) gate.arm();
  }, [active, gate, skip]);
  useLayoutEffect(() => {
    if (ready) gate.settle();
  }, [gate, ready]);

  return { gate, ready };
}

/**
 * `<img>` for transcript content: holds the gate until it loads. Mounted
 * while the gate is open, it loads eagerly: a lazy image outside the
 * viewport never fires `load`, and the pinned-to-bottom transcript puts most
 * of a long thread there.
 */
export function TranscriptImage(props: ComponentProps<"img">) {
  const gate = useContext(TranscriptLoadGateContext);
  const [loading] = useState<"eager" | "lazy">(() =>
    gate && !gate.isSettled ? "eager" : "lazy",
  );
  const ref = useCallback(
    (img: HTMLImageElement | null) => {
      if (!img || !gate) return undefined;
      return gate.holdImage(img);
    },
    [gate],
  );
  // eslint-disable-next-line @next/next/no-img-element
  return <img loading={loading} {...props} ref={ref} />;
}

/** `loading` for `next/dynamic` transcript chunks: holds the gate until the chunk renders. */
export function TranscriptChunkFallback() {
  const gate = useContext(TranscriptLoadGateContext);
  useLayoutEffect(() => gate?.hold(), [gate]);
  return null;
}
